import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";
import AspectWrapper from "core-web/components/AspectWrapper";
import BackgroundImage from "core-web/components/BackgroundImage";
import Link from "core-web/components/Link";
import { PLACEHOLDER } from "theme/config/constants";
import styled from "theme/libs/styled";
import ThemeButton from "theme/components/buttons/ThemeButton";
import PlayIcon from "theme/components/icons/PlayIcon";
import TextStyles from "theme/components/text/TextStyles";
import ModuleMarginWrapper from "theme/components/wrappers/ModuleMarginWrapper";
import ScalingGridWrapper from "theme/components/wrappers/ScalingGridWrapper";
import ArrowIcon from "theme/components/icons/ArrowIcon";

const Wrapper = styled("div")`
    margin: 24px 0;
`;

const Div = styled("div")`
    ${({ theme }) => theme.above.mobileLg} {
        margin-bottom: 24px;
    }
`;

const Content = styled("div")`
    height: 100%;
    display: flex;
    flex-direction: column;
    max-width: 520px;
`;

const Col = styled("div")`
    &[data-full-height="true"] {
        height: 100vh;
    }
`;

const ImageCol = styled(Col)`
    border-radius: 12px;
    overflow: hidden;

    ${({ theme }) => theme.above.tabletLg} {
        .background {
            transition: transform ${({ theme }) => theme.transitions.primarySlow};
        }
        &:hover .background {
            transform: scale(1.05);
        }
    }
`;

const VideoTag = styled("div")`
    position: absolute;
    top: 16px;
    left: 16px;
    padding: 7px 8px;
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.black};
    display: flex;
    align-items: center;

    border-radius: 100px;
`;

const FeaturedArticle = ({
    background,
    createdShort,
    heading,
    link,
    category,
    previewText,
    publishedAgo,
    tag,
    isLazyLoading,
}) => {
    const desktopImage = background?.mobile?.url;
    const mobileImage = background?.desktop?.url;
    const containsVideo = (tag || "").includes("video");
    const { t } = useTranslation();

    return (
        <Wrapper>
            <ModuleMarginWrapper>
                <ScalingGridWrapper
                    gridTemplateColumns={["1fr", null, null, null, "2fr 1fr"]}
                    columnGap={[null, null, null, null, "40px"]}
                >
                    <Link to={link}>
                        <ImageCol>
                            <AspectWrapper position="relative" ratio={4 / 3}>
                                {containsVideo && (
                                    <VideoTag>
                                        <TextStyles textStyles="Buenos Aires/14-120 Regular">
                                            {t("articles.video")}
                                        </TextStyles>
                                        <PlayIcon ml="5px" />
                                    </VideoTag>
                                )}
                                <BackgroundImage
                                    className="background"
                                    src={[
                                        mobileImage || desktopImage || PLACEHOLDER,
                                        null,
                                        null,
                                        desktopImage || mobileImage || PLACEHOLDER,
                                    ]}
                                    loading={isLazyLoading ? "lazy" : "eager"}
                                    query={{
                                        w: [768, 1024, 1440, 1920],
                                        q: 70,
                                    }}
                                />
                            </AspectWrapper>
                        </ImageCol>
                    </Link>
                    <Col>
                        <Content>
                            {(createdShort || category || publishedAgo) && (
                                <TextStyles mb={["12px", null, "24px"]} textStyles="Myriad Pro/14-140">
                                    {[createdShort, category, publishedAgo].filter(Boolean).join(" | ")}
                                </TextStyles>
                            )}
                            <Div>
                                {heading && (
                                    <TextStyles
                                        as="h2"
                                        mb={["12px", null, null, null, "16px"]}
                                        textStyles="Myriad Pro/24-130-semibold"
                                    >
                                        {decodeHTMLEntities(heading)}
                                    </TextStyles>
                                )}
                                {!!(previewText && previewText.length) && (
                                    <TextStyles textStyles="Myriad Pro/16-120">{previewText}</TextStyles>
                                )}
                                <ThemeButton
                                    mt={["16px", null, "24px"]}
                                    as={Link}
                                    to={link}
                                    type={null}
                                    buttonTheme="black_outlined"
                                    size="md"
                                    width="fit-content"
                                >
                                    {t("articles.read_more")}
                                    <ArrowIcon direction="right" />
                                </ThemeButton>
                            </Div>
                        </Content>
                    </Col>
                </ScalingGridWrapper>
            </ModuleMarginWrapper>
        </Wrapper>
    );
};

FeaturedArticle.propTypes = {
    background: PropTypes.shape({
        desktop: {
            url: PropTypes.string,
        },
        mobile: {
            url: PropTypes.string,
        },
    }),
    category: PropTypes.string,
    created: PropTypes.string,
    createdShort: PropTypes.string,
    publishedAgo: PropTypes.string,
    heading: PropTypes.string,
    isLazyLoading: PropTypes.bool,
    link: PropTypes.string,
    previewText: PropTypes.string.isRequired,
    tag: PropTypes.string,
};

FeaturedArticle.defaultProps = {
    background: {},
    category: "",
    created: null,
    createdShort: null,
    publishedAgo: null,
    heading: null,
    isLazyLoading: true,
    link: null,
    tag: "",
};

export default FeaturedArticle;
